import { FC, SyntheticEvent, useState } from "react";
import { useParams } from "react-router-dom";

import type { LiveTagStyling } from "./liveTag.types";
import { useTime } from "@/app/application/TimeContext";
import { Translate } from "@/app/application/Translate";
import { SavedLiveTagRequest } from "@/features/live/model/satos-live/settings/SavedTagValue";
import {
	useMutateSaveLiveTagValue,
	useUpdateStepStatus,
} from "@/features/procedures/api/checklist.mutations";
import { ListenerTag } from "@/features/procedures/model/procedure/ListenerTag";
import {
	ProcedureNodeStep,
	UpdateStepStatusRequest,
} from "@/features/procedures/model/types/checklist";
import { ProcedureButton } from "@/features/procedures/ui/components/common/ProcedureButton";
import { AlertConfirmStepOverride } from "@/shared/notifications/AlertConfirm";
import { Box, Chip, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { RouteParams } from "@/service/Utilities";

dayjs.extend(utc);

interface ListenerTagRowProps {
	isLast: boolean;
	listenerTag: ListenerTag;
	step: ProcedureNodeStep;
	checklistId: number | undefined;
	styling: LiveTagStyling;
}

export const ListenerTagRow: FC<ListenerTagRowProps> = ({
	listenerTag,
	step,
	isLast,
	checklistId,
	styling,
}) => {
	const { id } = useParams<RouteParams>();
	const procedureId = Number(id);
	const { timezone } = useTime();
	const updateStepState = useUpdateStepStatus();
	const saveLiveTagValue = useMutateSaveLiveTagValue();
	const [isOverriding, setIsOverriding] = useState(false);

	const formatTimestamp = (timestamp: string | null | undefined) => {
		if (!timestamp) return "";
		return dayjs.utc(timestamp).tz(timezone).format("DD/MM/YY HH:mm:ss");
	};

	const value = listenerTag.SavedTagValue
		? listenerTag.SavedTagValue
		: listenerTag.CurrentValue;

	const saveStatusAndValue = async () => {
		if (isOverriding) return;
		setIsOverriding(true);
		const request: UpdateStepStatusRequest = {
			ArchiveType: "Status",
			StepId: step.Id,
			ProcedureId: procedureId,
			File: null,
			SchemeId: checklistId,
			IsOverridden: true,
			Status: 1,
			Metadata: { ...step, Status: 0 },
		};

		try {
			await updateStepState(request);

			if (listenerTag.CurrentValue) {
				const saveTagRequest: SavedLiveTagRequest = {
					StepId: step.Id,
					ProcedureId: procedureId,
					SchemeId: checklistId,
					SavedTagValue: listenerTag.CurrentValue,
					Tag: listenerTag?.Tag,
					TagId: listenerTag?.Id,
				};

				await saveLiveTagValue(saveTagRequest);
			}
		} finally {
			setIsOverriding(false);
		}
	};

	const handleOverride = (e: SyntheticEvent) => {
		e.preventDefault();

		AlertConfirmStepOverride(() => {
			void saveStatusAndValue();
		});
	};

	return (
		<Box sx={{ display: "flex", gap: "3rem", overflow: "visible" }}>
			<Box
				sx={{
					textAlign: "center",
				}}
			>
				<Chip
					label={
						<>
							<Box
								sx={{
									borderRadius: "50%",
								}}
							></Box>
							{value !== null ? (
								<>
									{listenerTag?.Name}
									<Translate String="Procedure.ListenerTagRow.Live">
										{" "}
										Live:{" "}
									</Translate>
									{value}
								</>
							) : (
								<>
									{listenerTag?.Name}{" "}
									<Translate String="Procedure.ListenerTagRow.UnableToRetrieveLiveData">
										Live: Unable to retrieve live data
									</Translate>
								</>
							)}
						</>
					}
					sx={{
						display: "flex",
						justifyContent: "space-between",
						verticalAlign: "center",
						width: "fit-content",
						transition: ".2s all ease-in-out",
						position: "relative",
						overflow: "hidden",
						...styling?.container,
						"&:before": styling?.pulseAnimation,
					}}
				/>
				{!Number.isNaN(listenerTag?.SavedTagValue) &&
				listenerTag.SavedTagValue ? (
					<Typography variant="caption">
						Value is from: {formatTimestamp(listenerTag?.SavedTagTimestamp)}
					</Typography>
				) : step.IsOverridden ? (
					<Typography variant="caption">
						<Translate String="Checklist.Step.Overridden">
							Step was overridden
						</Translate>
					</Typography>
				) : null}
			</Box>

			{isLast && !step.Status && (
				<Box>
					<ProcedureButton
						variant="outlined"
						color="warning"
						size="small"
						onClick={handleOverride}
						loading={isOverriding}
						disabled={isOverriding}
						loadingText={
							<Translate String="Checklist.Step.Overriding">
								Overriding...
							</Translate>
						}
						sx={{
							height: "30px",
						}}
					>
						<Translate String="Checklist.Step.Override">
							Override step
						</Translate>
					</ProcedureButton>
				</Box>
			)}
		</Box>
	);
};
